// Tiny UI-only coordination flag for moments when a native system UI surface (camera
// picker, share sheet, etc.) is expected to briefly background the host app. Capacitor's
// `appStateChange` event fires with `isActive: false` in exactly the same shape whether the
// user genuinely backgrounded the app or the OS just took over the screen for a picker/share
// sheet — consumers that react to backgrounding (e.g. `AppLockGate`) need a way to tell
// "expected hand-off to system UI" apart from "user left the app" and skip their reaction
// while a hand-off is in progress.
//
// A plain module-level counter (not a hook/context) since this is transient, synchronous,
// cross-cutting UI state read from a listener outside React's render cycle — mirrors the
// listener-list pattern already used by `useBiometricLock`/`useToast`. A counter (rather than
// a boolean) tolerates overlapping hand-offs without one clearing the other early.
let handoffCount = 0

// ~60s: long enough that a user actively browsing/selecting in a camera roll or share sheet
// (typically a few seconds, occasionally up to roughly a minute for a slow, deliberate pick)
// never gets force re-locked the moment they return — the exact "lock fires on every camera
// tap" annoyance this hand-off flag exists to prevent. Short enough to still catch a genuine
// backgrounding (phone call, switching to another app, or simply leaving the picker open and
// walking away) that happens to occur while nominally "mid hand-off".
const HANDOFF_BACKGROUND_LOCK_THRESHOLD_MS = 60_000

// Timestamp of the earliest `beginNativeUiHandoff()` call while a hand-off is open — only the
// first is tracked (not one per call), and it's left in place after the hand-off closes so the
// resume-time overstay check below still has something to measure against.
let handoffStartedAt: number | undefined

// Whether an `appStateChange` -> `isActive: false` was observed at any point during the
// current (or just-closed) hand-off — i.e. the picker/share sheet actually took the host app
// out of the foreground, as opposed to the JS side merely awaiting a promise.
let backgroundedDuringHandoff = false

export function beginNativeUiHandoff(): void {
  if (handoffCount === 0) {
    handoffStartedAt = Date.now()
    backgroundedDuringHandoff = false
  }
  handoffCount += 1
}

export function endNativeUiHandoff(): void {
  handoffCount = Math.max(0, handoffCount - 1)
}

export function isNativeUiHandoffInProgress(): boolean {
  return handoffCount > 0
}

/** Call from the `appStateChange` listener when the app backgrounds while a hand-off is in
 *  progress. Just records the fact — the caller (`AppLockGate`) deliberately does not lock
 *  immediately, only once it knows (via `consumeHandoffOverstay`) whether the backgrounding
 *  turned out to be a short picker/share transition or a genuinely long one. */
export function noteHandoffBackgrounded(): void {
  if (handoffCount > 0) backgroundedDuringHandoff = true
}

/** Call from the `appStateChange` listener when the app resumes. Returns `true` (and resets
 *  the tracked state) exactly once if the hand-off the app is returning from backgrounded it
 *  for longer than `HANDOFF_BACKGROUND_LOCK_THRESHOLD_MS` — at that point it's no longer
 *  distinguishable from the user genuinely leaving the app, so the caller should lock
 *  retroactively. Returns `false` for short hand-offs (the common case) so they stay silent. */
export function consumeHandoffOverstay(): boolean {
  const overstayed =
    backgroundedDuringHandoff &&
    handoffStartedAt !== undefined &&
    Date.now() - handoffStartedAt >= HANDOFF_BACKGROUND_LOCK_THRESHOLD_MS
  backgroundedDuringHandoff = false
  return overstayed
}
